/**
 * RoofControls - Roof polygon selection and editing controls
 * Includes polygon visibility toggle for "other" polygons
 * Includes edge selector and area calculations
 */

import React from 'react';
import { Square, Edit3, Trash2, PenTool, Save, X, Home, Plus } from 'lucide-react';
import { AuroraCard, AuroraCardSection, AuroraButton, AuroraToggle } from './AuroraCard';
import { EdgeSelectorControls } from './EdgeSelectorControls';

interface RoofControlsProps {
  // Manual mode flag - when true, roof tools are disabled
  isManualMode?: boolean;

  // Polygon selection
  selectedPolygon: number | null;
  polygons: any[];
  onSelectPolygon: (id: number | null) => void;

  // Selection mode (enables clicking polygons on map)
  isSelectMode?: boolean;
  onToggleSelectMode?: () => void;

  // Add Roof mode (multi-roof without replacing existing)
  isAddRoofMode?: boolean;
  onToggleAddRoofMode?: () => void;

  // Phase 3: Multi-roof selection (always enabled)
  selectedPolygons?: number[];
  activeRoofId?: number | null;

  // Edit mode
  isEditing: boolean;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;

  // Delete/Redraw/Convert/Reset
  onDeletePolygon: () => void;
  onStartRedraw: () => void;
  onConvertToRoof?: () => void;
  onResetPolygons?: () => void;
  isRoofEstablished?: boolean;

  // Polygon visibility
  showAllPolygons: boolean;
  onToggleAllPolygons: (show: boolean) => void;

  // Area calculations
  grossArea?: number;
  obstructionArea?: number;
  netArea?: number;

  // Edge selector
  isSelectingEdge: boolean;
  onStartEdgeSelection: () => void;
  onStopEdgeSelection: () => void;
  selectedEdgeIndex?: number;
  edgeAzimuth?: number | null;
  manualAzimuth?: number | null;
  onManualAzimuthChange: (azimuth: number | null) => void;

  // Measurements
  showMeasurements: boolean;
  onToggleMeasurements: (show: boolean) => void;

  // Collapse state
  collapsed?: boolean;
  onToggle?: () => void;
}

export const RoofControls: React.FC<RoofControlsProps> = ({
  isManualMode = false,
  selectedPolygon,
  polygons,
  onSelectPolygon,
  isSelectMode = false,
  onToggleSelectMode,
  isAddRoofMode = false,
  onToggleAddRoofMode,
  selectedPolygons = [],
  activeRoofId = null,
  isEditing,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDeletePolygon,
  onStartRedraw,
  onConvertToRoof,
  onResetPolygons,
  isRoofEstablished = false,
  showAllPolygons,
  onToggleAllPolygons,
  grossArea,
  obstructionArea = 0,
  netArea,
  isSelectingEdge,
  onStartEdgeSelection,
  onStopEdgeSelection,
  selectedEdgeIndex,
  edgeAzimuth,
  manualAzimuth,
  onManualAzimuthChange,
  showMeasurements,
  onToggleMeasurements,
  collapsed = false,
  onToggle,
}) => {
  // In manual mode, show a message instead of roof tools
  if (isManualMode) {
    return (
      <AuroraCard
        title="Roof"
        collapsed={collapsed}
        onToggle={onToggle}
      >
        <AuroraCardSection>
          <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-orange-700">
              <Home className="h-5 w-5" />
              <span className="font-semibold text-sm">Manual Mode Active</span>
            </div>
            <p className="text-xs text-gray-700">
              Roof polygon detection is disabled in manual mode.
            </p>
            <p className="text-xs text-gray-600">
              Use <span className="font-semibold">Free-Field Segments</span> to draw solar arrays directly on the map, or switch to <span className="font-semibold">Auto Mode</span> to detect roof polygons automatically.
            </p>
            <div className="pt-2 border-t border-orange-200">
              <p className="text-xs text-orange-600 font-medium">
                Tip: Go to System mode → Draw Free-Field Segment
              </p>
            </div>
          </div>
        </AuroraCardSection>
      </AuroraCard>
    );
  }

  return (
    <AuroraCard
      title="Roof"
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <AuroraCardSection>
        {/* Visibility & Measurement Toggles */}
        <div className="mb-4 pb-4 border-b-2 border-gray-200 space-y-2">
          <AuroraToggle
            label="Show Measurements"
            checked={showMeasurements}
            onChange={onToggleMeasurements}
          />
          <AuroraToggle
            label="Show Other Polygons"
            checked={showAllPolygons}
            onChange={onToggleAllPolygons}
          />
        </div>

        {/* Phase 3: Selected Roofs List (Multi-Selection Mode) */}
        {selectedPolygons.length > 1 && (
          <div className="mb-4 pb-4 border-b-2 border-gray-200">
            <label className="text-xs text-gray-600 mb-2 block font-semibold">
              Selected Roofs ({selectedPolygons.length})
            </label>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {selectedPolygons.map(polygonId => {
                const polygon = polygons.find(p => p.id === polygonId);
                const isActive = activeRoofId === polygonId;

                return (
                  <div
                    key={polygonId}
                    onClick={() => onSelectPolygon(polygonId)}
                    className={`
                      p-2 rounded-lg border-2 cursor-pointer transition-all
                      ${isActive
                        ? 'bg-[#EBFD03]/30 border-[#EBFD03] shadow-md'
                        : 'bg-gray-50 border-gray-300 hover:border-gray-400'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${isActive ? 'text-black' : 'text-gray-700'}`}>
                            Roof #{polygonId}
                          </span>
                          {isActive && (
                            <span className="text-xs bg-[#EBFD03] text-black px-2 py-0.5 rounded-full font-semibold">
                              ACTIVE
                            </span>
                          )}
                        </div>
                        {polygon && (
                          <div className="text-xs text-gray-600 mt-0.5">
                            {polygon.area_m2?.toFixed(1) || 'N/A'} m²
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Remove from selection by toggling
                          const newSelection = selectedPolygons.filter(id => id !== polygonId);
                          if (newSelection.length > 0) {
                            onSelectPolygon(newSelection[0]);
                          } else {
                            onSelectPolygon(null);
                          }
                        }}
                        className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove from selection"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Click a roof to make it active for module placement
            </p>
          </div>
        )}

        {/* Polygon Selection & Area */}
        <div className="mb-4">
          <label className="text-xs text-gray-600 mb-2 block font-semibold">
            {selectedPolygons.length > 1 ? 'Active Roof' : 'Selected Polygon'}
          </label>

          {selectedPolygon === null ? (
            <div className="text-sm text-gray-600 italic bg-gray-50 rounded-lg p-3 border-2 border-gray-200">
              Click a polygon on the map to select, or draw a new roof
            </div>
          ) : (
            <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-3 space-y-2">
              <div>
                <div className="text-sm font-semibold text-black">
                  Polygon #{selectedPolygon}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Click "Convert to Roof" to use this polygon
                </div>
              </div>

              {/* Area Calculations */}
              {grossArea !== undefined && (
                <div className="text-xs text-gray-700 space-y-1 pt-2 border-t border-gray-300">
                  <div className="flex justify-between">
                    <span className="font-semibold">Gross Area:</span>
                    <span>{grossArea.toFixed(2)} m²</span>
                  </div>
                  {obstructionArea > 0 && (
                    <>
                      <div className="flex justify-between text-red-600">
                        <span className="font-semibold">Obstructions:</span>
                        <span>-{obstructionArea.toFixed(2)} m²</span>
                      </div>
                      <div className="flex justify-between text-green-600 font-semibold border-t border-gray-300 pt-1">
                        <span>Net Usable:</span>
                        <span>{(netArea || (grossArea - obstructionArea)).toFixed(2)} m²</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Convert to Roof Button or Status */}
              {onConvertToRoof && (
                <div className="pt-2 border-t border-gray-300">
                  {!isRoofEstablished ? (
                    <>
                      <AuroraButton
                        variant="primary"
                        size="md"
                        onClick={onConvertToRoof}
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <Home className="h-4 w-4" />
                        <span>Convert to Roof</span>
                      </AuroraButton>
                      <p className="text-xs text-gray-600 mt-1">
                        Use this polygon as the active roof
                      </p>
                    </>
                  ) : (
                    <div className="bg-green-50 border-2 border-green-500 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-green-700 text-sm font-semibold">
                        <Home className="h-4 w-4" />
                        <span>Roof Established</span>
                      </div>
                      <p className="text-xs text-gray-700 mt-1">
                        This polygon is now your active roof
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Edit Controls */}
        {selectedPolygon !== null && (
          <div className="space-y-2">
            {!isEditing ? (
              <>
                {/* Select Roof Mode Button */}
                {onToggleSelectMode && (
                  <AuroraButton
                    variant={isSelectMode ? "primary" : "secondary"}
                    size="md"
                    onClick={onToggleSelectMode}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Square className="h-4 w-4" />
                    <span>{isSelectMode ? 'Exit Select Mode' : 'Select Roof'}</span>
                  </AuroraButton>
                )}

                {isSelectMode && (
                  <div className="bg-[#EBFD03]/20 border-2 border-[#EBFD03] rounded-lg p-2 text-xs text-gray-700">
                    👆 Click to toggle roof selection (multi-select enabled)
                  </div>
                )}

                {/* Add Roof Mode Button - For multi-roof without replacing */}
                {onToggleAddRoofMode && (
                  <AuroraButton
                    variant={isAddRoofMode ? "primary" : "secondary"}
                    size="md"
                    onClick={onToggleAddRoofMode}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>{isAddRoofMode ? 'Exit Add Roof' : 'Add Roof'}</span>
                  </AuroraButton>
                )}

                {isAddRoofMode && (
                  <div className="bg-blue-100 border-2 border-blue-400 rounded-lg p-2 text-xs text-gray-700">
                    ➕ Click another roof to add it (keeps existing modules)
                  </div>
                )}

                {/* Edit Mode Button */}
                <AuroraButton
                  variant="primary"
                  size="md"
                  onClick={onStartEdit}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  <span>Edit Polygon</span>
                </AuroraButton>

                {/* Delete Button */}
                <AuroraButton
                  variant="danger"
                  size="md"
                  onClick={onDeletePolygon}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete Polygon</span>
                </AuroraButton>
              </>
            ) : (
              <>
                {/* Edit Mode Active */}
                <div className="bg-[#EBFD03]/20 border-2 border-[#EBFD03] rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 text-black text-sm font-semibold mb-1">
                    <Edit3 className="h-4 w-4" />
                    <span>Edit Mode Active</span>
                  </div>
                  <p className="text-xs text-gray-700">
                    Drag vertices to reshape the polygon. Original outline shown in gray.
                  </p>
                </div>

                {/* Save/Cancel Buttons */}
                <div className="flex gap-2">
                  <AuroraButton
                    variant="primary"
                    size="md"
                    onClick={onSaveEdit}
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>Save</span>
                  </AuroraButton>

                  <AuroraButton
                    variant="secondary"
                    size="md"
                    onClick={onCancelEdit}
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    <span>Cancel</span>
                  </AuroraButton>
                </div>
              </>
            )}
          </div>
        )}

        {/* Edge Selector - Only when polygon is selected and not editing */}
        {selectedPolygon !== null && !isEditing && (
          <div className="mb-4 pb-4 border-t-2 border-gray-200 pt-4">
            <EdgeSelectorControls
              isSelectingEdge={isSelectingEdge}
              onStartEdgeSelection={onStartEdgeSelection}
              onStopEdgeSelection={onStopEdgeSelection}
              selectedEdgeIndex={selectedEdgeIndex}
              edgeAzimuth={edgeAzimuth}
              manualAzimuth={manualAzimuth}
              onManualAzimuthChange={onManualAzimuthChange}
            />
          </div>
        )}

        {/* Draw New Roof & Reset */}
        {!isEditing && (
          <div className="mt-4 pt-4 border-t-2 border-gray-200 space-y-2">
            <AuroraButton
              variant="secondary"
              size="md"
              onClick={onStartRedraw}
              className="w-full flex items-center justify-center gap-2"
            >
              <PenTool className="h-4 w-4" />
              <span>Draw New Roof</span>
            </AuroraButton>

            {/* Reset Polygons Button */}
            {onResetPolygons && (
              <AuroraButton
                variant="secondary"
                size="md"
                onClick={onResetPolygons}
                className="w-full flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                <span>Reset Polygons</span>
              </AuroraButton>
            )}
          </div>
        )}
      </AuroraCardSection>
    </AuroraCard>
  );
};
